"use client";

import React, { useState } from "react";
import { useToast } from "@/app/hooks/toast/useToast";
import InputField from "../../../components/common/InputField";
import { TextAreaField } from "../../../components/common/TextAreaField/TextAreaField";
import { useLocale, useTranslations } from "next-intl";

type FormData = {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  phone?: string;
};

const DEFAULT_DATA: FormData = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
};

export const ContactForm: React.FC = () => {
  const { showToast } = useToast();
  const t = useTranslations();
  const [data, setData] = useState<FormData>(DEFAULT_DATA);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [loading, setLoading] = useState(false);
  const locale = useLocale();

  const validate = (): Partial<FormData> => {
    const errs: Partial<FormData> = {};
    if (!data.firstName.trim()) errs.firstName = t("Other.firstNameIsRequired");
    if (!data.lastName.trim()) errs.lastName = t("Other.lastNameIsRequired");
    if (!/^\S+@\S+\.\S+$/.test(data.email))
      errs.email = t("Other.validEmailIsRequired");
    if (!data.message.trim()) errs.message = t("Other.messageCantBeEmpty");
    return errs;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const name = e.target.name as keyof FormData;
    const value = e.target.value;

    setData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      if (!(name in prev)) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`/api/contact?lang=${locale}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw res;
      showToast(t("Other.messageSent"), "success");
      setData(DEFAULT_DATA);
      setErrors({});
    } catch (err: any) {
      showToast(
        err?.response?.data?.error ||
          err?.response?.statusText ||
          t("Other.errorOccurred"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="self-stretch py-4 flex flex-col gap-3 w-full lg:py-6"
    >
      <h2 className="text-xl font-bold font-switzer text-zinc-900 dark:text-white">
        {t("Other.contactForm")}
      </h2>

      <InputField
        name="firstName"
        value={data.firstName}
        onChange={handleChange}
        placeholder={t("Other.firstName")}
      />
      {errors.firstName && (
        <p className="text-sm text-red-500">{errors.firstName}</p>
      )}

      <InputField
        name="lastName"
        value={data.lastName}
        onChange={handleChange}
        placeholder={t("Other.lastName")}
      />
      {errors.lastName && (
        <p className="text-sm text-red-500">{errors.lastName}</p>
      )}

      <InputField
        name="email"
        type="email"
        value={data.email}
        onChange={handleChange}
        placeholder={t("Other.emailAddress")}
      />
      {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}

      <TextAreaField
        name="message"
        value={data.message}
        onChange={handleChange}
        placeholder={t("Other.yourMessage")}
      />
      {errors.message && (
        <p className="text-sm text-red-500">{errors.message}</p>
      )}

      <button
        type="submit"
        disabled={loading}
        className="self-stretch h-12 px-6 py-2 bg-sleap-purple-300 rounded-full text-white font-semibold hover:brightness-90 transition w-full font-switzer lg:self-start lg:w-auto lg:flex-0"
      >
        {loading ? t("Other.sending") : t("Other.sendMessage")}
      </button>
    </form>
  );
};
